import { useState, type ReactNode } from 'react';

import { AgentCard } from './AgentCard';
import { AgentDetail } from './AgentDetail';
import { CreateAgentModal } from './CreateAgentModal';

export interface AgentSummary {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  role: { id: string; slug: string; name: string };
  has_agent_md: boolean;
  has_soul_md: boolean;
  skill_count: number;
  status: 'active' | 'deactivated';
  created_at: string;
  updated_at: string;
}

export interface AgentDetailData extends AgentSummary {
  frontmatter: Record<string, unknown>;
  role_capabilities: Record<string, boolean>;
  granted_skills: Array<{ id: string; name: string }>;
  created_by_email: string;
}

export interface RoleOption {
  id: string;
  slug: string;
  name: string;
  capabilities: Record<string, boolean>;
}

export interface AgentsFilters {
  search: string;
  roleId: string | null;
  status: 'active' | 'deactivated' | 'all';
}

interface Props {
  agents: AgentSummary[];
  status: 'idle' | 'loading' | 'success' | 'error';
  error?: string;
  filters: AgentsFilters;
  setFilters: (next: AgentsFilters) => void;
  roles: RoleOption[];
  selectedAgentId: string | null;
  selectedAgent: AgentDetailData | null;
  selectedAgentStatus: 'idle' | 'loading' | 'success' | 'error';
  selectedAgentError?: string;
  onSelect: (id: string | null) => void;
  onCreate: (input: {
    name: string;
    slug: string;
    description: string;
    role_id: string;
    include_soul_md: boolean;
  }) => Promise<void> | void;
  onDeactivate: (id: string) => Promise<void> | void;
  canManage: boolean;
  /**
   * Optional factory that yields the Code & Revisions tab body for a given
   * loaded agent. Kept as a render prop so this presentational component
   * doesn't import the data hooks directly.
   */
  codeTabFor?: (agent: AgentDetailData) => ReactNode;
}

export function AgentsView({
  agents,
  status,
  error,
  filters,
  setFilters,
  roles,
  selectedAgentId,
  selectedAgent,
  selectedAgentStatus,
  selectedAgentError,
  onSelect,
  onCreate,
  onDeactivate,
  canManage,
  codeTabFor,
}: Props) {
  const [createOpen, setCreateOpen] = useState(false);

  const showEmpty = status === 'success' && agents.length === 0;
  const filtersActive = filters.search.length > 0 || filters.roleId !== null;

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <FilterBar
        filters={filters}
        setFilters={setFilters}
        roles={roles}
        agentCount={agents.length}
        canManage={canManage}
        onNewAgent={() => setCreateOpen(true)}
      />

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '24px 24px 32px',
        }}
      >
        {status === 'loading' && (
          <div style={statusMessageStyle}>Loading roster&hellip;</div>
        )}
        {status === 'error' && (
          <div style={{ ...statusMessageStyle, color: 'var(--kind-failure)' }}>
            {error ?? 'Failed to load agents.'}
          </div>
        )}

        {showEmpty && !filtersActive && <EmptyState canManage={canManage} onNewAgent={() => setCreateOpen(true)} />}
        {showEmpty && filtersActive && (
          <div style={statusMessageStyle}>No agents match the current filters.</div>
        )}

        {agents.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 18,
            }}
          >
            {agents.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                selected={agent.id === selectedAgentId}
                onClick={() => onSelect(agent.id)}
              />
            ))}
          </div>
        )}
      </div>

      {selectedAgentId && (
        <AgentDetail
          agentId={selectedAgentId}
          detail={selectedAgent}
          status={selectedAgentStatus}
          {...(selectedAgentError !== undefined ? { error: selectedAgentError } : {})}
          canManage={canManage}
          onClose={() => onSelect(null)}
          onDeactivate={onDeactivate}
          {...(codeTabFor && selectedAgent ? { codeTab: codeTabFor(selectedAgent) } : {})}
        />
      )}

      {createOpen && (
        <CreateAgentModal
          roles={roles}
          onCancel={() => setCreateOpen(false)}
          onSubmit={async (input) => {
            await onCreate(input);
            setCreateOpen(false);
          }}
        />
      )}
    </div>
  );
}

interface FilterBarProps {
  filters: AgentsFilters;
  setFilters: (next: AgentsFilters) => void;
  roles: RoleOption[];
  agentCount: number;
  canManage: boolean;
  onNewAgent: () => void;
}

function FilterBar({ filters, setFilters, roles, agentCount, canManage, onNewAgent }: FilterBarProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '16px 24px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)',
        flexWrap: 'wrap',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          fontFamily: HEADING_SERIF,
          fontSize: 20,
          fontWeight: 500,
          color: 'var(--text)',
          letterSpacing: '-0.01em',
        }}
      >
        Agents
      </div>
      <div
        style={{
          fontFamily: MONO,
          fontSize: 10,
          color: 'var(--text-soft)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          paddingTop: 4,
        }}
      >
        {agentCount.toString().padStart(2, '0')}&nbsp;on roster
      </div>

      <input
        placeholder="Filter by name or slug&hellip;"
        value={filters.search}
        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        style={{
          marginLeft: 'auto',
          padding: '6px 10px',
          fontSize: 12,
          border: '1px solid var(--border-strong)',
          borderRadius: 6,
          background: 'var(--bg)',
          color: 'var(--text)',
          outline: 'none',
          fontFamily: 'inherit',
          minWidth: 220,
        }}
      />
      <select
        value={filters.roleId ?? ''}
        onChange={(e) => setFilters({ ...filters, roleId: e.target.value || null })}
        style={selectStyle}
        aria-label="Filter by role"
      >
        <option value="">All roles</option>
        {roles.map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
          </option>
        ))}
      </select>
      <select
        value={filters.status}
        onChange={(e) =>
          setFilters({ ...filters, status: e.target.value as 'active' | 'deactivated' | 'all' })
        }
        style={selectStyle}
        aria-label="Filter by status"
      >
        <option value="active">Active</option>
        <option value="deactivated">Deactivated</option>
        <option value="all">All</option>
      </select>
      {canManage && (
        <button
          type="button"
          onClick={onNewAgent}
          style={primaryButtonStyle}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'var(--accent-hover, #b55a37)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'var(--accent)';
          }}
        >
          + New Agent
        </button>
      )}
    </div>
  );
}

function EmptyState({ canManage, onNewAgent }: { canManage: boolean; onNewAgent: () => void }) {
  // DESIGN_DEVIATION: pure-CSS stamped glyph instead of SVG illustration. There is no Skills-side
  // parallel — Skills view has no comparable empty state. This is the brief's "one chance to surprise."
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '80px 24px',
        minHeight: 360,
      }}
    >
      <StampedGlyph />
      <div
        style={{
          marginTop: 28,
          fontFamily: HEADING_SERIF,
          fontSize: 22,
          fontWeight: 500,
          color: 'var(--text)',
          letterSpacing: '-0.015em',
        }}
      >
        An empty roster.
      </div>
      <div
        style={{
          marginTop: 8,
          maxWidth: 360,
          fontSize: 13,
          lineHeight: 1.55,
          color: 'var(--text-soft)',
        }}
      >
        Provision a non-human operator to take over routine work. Agents read mail, draft replies, and queue tasks for your approval.
      </div>
      {canManage && (
        <button
          type="button"
          onClick={onNewAgent}
          style={{
            ...primaryButtonStyle,
            marginTop: 24,
            padding: '9px 18px',
            fontSize: 13,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'var(--accent-hover, #b55a37)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'var(--accent)';
          }}
        >
          Provision the first agent
        </button>
      )}
    </div>
  );
}

function StampedGlyph() {
  return (
    <div
      aria-hidden
      style={{
        width: 88,
        height: 88,
        borderRadius: '50%',
        position: 'relative',
        background: 'var(--surface)',
        border: '1px solid var(--border-strong)',
        boxShadow:
          'inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -1px 0 rgba(0,0,0,0.04), 0 1px 0 rgba(255,255,255,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 8,
          borderRadius: '50%',
          border: '1px dashed var(--border-strong)',
        }}
      />
      <div
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: 'var(--accent)',
          boxShadow: '0 0 0 4px var(--bg)',
        }}
      />
    </div>
  );
}

const statusMessageStyle: React.CSSProperties = {
  padding: 24,
  fontSize: 13,
  color: 'var(--text-mute)',
  textAlign: 'center',
};

const selectStyle: React.CSSProperties = {
  padding: '6px 10px',
  fontSize: 12,
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  background: 'var(--bg)',
  color: 'var(--text)',
  outline: 'none',
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const primaryButtonStyle: React.CSSProperties = {
  padding: '7px 13px',
  background: 'var(--accent)',
  color: '#fff',
  border: 0,
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
  fontWeight: 500,
  fontFamily: 'inherit',
  letterSpacing: '0.01em',
  transition: 'background 120ms ease',
};

const HEADING_SERIF =
  "ui-serif, Georgia, 'Iowan Old Style', 'Apple Garamond', Palatino, serif";
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
